import React from 'react'
import { cn } from '@/lib/utils'
import { BaseSlideLayout, SlideHeader, SlideContent, SlideFooter, ContentGrid } from '../layouts/BaseSlideLayout'
import { Card, CardContent } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'

/**
 * STANDARDIZED SLIDE TEMPLATES
 *
 * These templates provide consistent, reusable slide patterns.
 * Use these instead of creating custom layouts for each slide.
 */

// ============================================
// TITLE SLIDE
// ============================================
export interface StandardTitleSlideProps {
  title: string
  subtitle?: string
  author?: string
  date?: string
  tags?: string[]
  footer?: React.ReactNode
  background?: 'primary' | 'secondary' | 'accent'
}

export const StandardTitleSlide: React.FC<StandardTitleSlideProps> = ({
  title,
  subtitle,
  author,
  date,
  tags,
  footer,
  background = 'primary',
}) => {
  return (
    <BaseSlideLayout background={background}>
      <SlideContent centered>
        {/* Tags */}
        {tags && tags.length > 0 && (
          <div className="flex flex-wrap gap-2 mb-6 justify-center animate-fade-in">
            {tags.map((tag, index) => (
              <Badge key={index} className="bg-white/20 text-white border-white/30 hover:bg-white/30">
                {tag}
              </Badge>
            ))}
          </div>
        )}

        {/* Title */}
        <h1 className="text-5xl md:text-6xl lg:text-7xl font-bold mb-6 text-center animate-fade-in delay-1 leading-tight">
          {title}
        </h1>

        {/* Subtitle */}
        {subtitle && (
          <p className="text-xl md:text-2xl lg:text-3xl text-center mb-12 animate-fade-in delay-2 opacity-90 max-w-4xl">
            {subtitle}
          </p>
        )}

        {/* Author & Date */}
        {(author || date) && (
          <div className="text-center animate-fade-in delay-3 opacity-90">
            {author && <p className="text-lg font-semibold mb-1">{author}</p>}
            {date && <p className="text-base">{date}</p>}
          </div>
        )}

        {/* Footer Content */}
        {footer && (
          <div className="mt-12 animate-fade-in delay-4">
            {footer}
          </div>
        )}
      </SlideContent>
    </BaseSlideLayout>
  )
}

// ============================================
// CONTENT SLIDE (Single Column)
// ============================================
export interface StandardContentSlideProps {
  title: string
  subtitle?: string
  children: React.ReactNode
  background?: 'primary' | 'secondary' | 'accent' | 'light' | 'dark'
  footer?: React.ReactNode
}

export const StandardContentSlide: React.FC<StandardContentSlideProps> = ({
  title,
  subtitle,
  children,
  background = 'light',
  footer,
}) => {
  return (
    <BaseSlideLayout background={background}>
      <SlideHeader title={title} subtitle={subtitle} />
      <SlideContent>
        {children}
      </SlideContent>
      {footer && <SlideFooter>{footer}</SlideFooter>}
    </BaseSlideLayout>
  )
}

// ============================================
// GRID SLIDE (2, 3, or 4 columns)
// ============================================
export interface StandardGridSlideProps {
  title: string
  subtitle?: string
  items: React.ReactNode[]
  columns?: 2 | 3 | 4
  background?: 'primary' | 'secondary' | 'accent' | 'light' | 'dark'
  footer?: React.ReactNode
  animated?: boolean
}

export const StandardGridSlide: React.FC<StandardGridSlideProps> = ({
  title,
  subtitle,
  items,
  columns = 2,
  background = 'light',
  footer,
  animated = true,
}) => {
  return (
    <BaseSlideLayout background={background}>
      <SlideHeader title={title} subtitle={subtitle} />
      <SlideContent>
        <ContentGrid columns={columns} gap="md">
          {items.map((item, index) => (
            <div
              key={index}
              className={cn(
                animated && 'animate-fade-in',
                animated && index < 5 && `delay-${index + 1}`
              )}
            >
              {item}
            </div>
          ))}
        </ContentGrid>
      </SlideContent>
      {footer && <SlideFooter>{footer}</SlideFooter>}
    </BaseSlideLayout>
  )
}

// ============================================
// FEATURE CARD (Reusable Component)
// ============================================
export interface FeatureCardProps {
  icon?: React.ReactNode
  title: string
  description?: string
  items?: string[]
  variant?: 'default' | 'glass' | 'elevated'
  iconColor?: string
}

export const FeatureCard: React.FC<FeatureCardProps> = ({
  icon,
  title,
  description,
  items,
  variant = 'default',
  iconColor = 'text-primary-600 dark:text-primary-400',
}) => {
  const variantClasses = {
    default: 'bg-card border-border',
    glass: 'bg-white/10 dark:bg-black/10 backdrop-blur-sm border-white/20',
    elevated: 'bg-card shadow-lg border-border',
  }

  return (
    <Card className={cn('h-full flex flex-col', variantClasses[variant])}>
      <CardContent className="flex-1">
        {icon && (
          <div className={cn('mb-4 flex justify-start', iconColor)}>
            {icon}
          </div>
        )}
        <h3 className="text-xl font-bold mb-3 text-left">{title}</h3>
        {description && (
          <p className="text-sm opacity-90 mb-3 text-left">{description}</p>
        )}
        {items && items.length > 0 && (
          <ul className="space-y-2 text-sm text-left">
            {items.map((item, index) => (
              <li key={index} className="flex items-start gap-3">
                <span className="text-primary-600 dark:text-primary-400 mt-0.5 flex-shrink-0 leading-relaxed">•</span>
                <span className="opacity-90 flex-1 leading-relaxed">{item}</span>
              </li>
            ))}
          </ul>
        )}
      </CardContent>
    </Card>
  )
}

// ============================================
// INFO BOX (Reusable Component for highlights)
// ============================================
export interface InfoBoxProps {
  children: React.ReactNode
  variant?: 'info' | 'success' | 'warning' | 'tip'
  icon?: React.ReactNode
  className?: string
}

export const InfoBox: React.FC<InfoBoxProps> = ({
  children,
  variant = 'info',
  icon,
  className,
}) => {
  const variants = {
    info: {
      bg: 'bg-blue-50 dark:bg-blue-900/20 border-blue-600',
      iconColor: 'text-blue-600',
    },
    success: {
      bg: 'bg-green-50 dark:bg-green-900/20 border-green-600',
      iconColor: 'text-green-600',
    },
    warning: {
      bg: 'bg-yellow-50 dark:bg-yellow-900/20 border-yellow-600',
      iconColor: 'text-yellow-600',
    },
    tip: {
      bg: 'bg-blue-50 dark:bg-blue-900/20 border-blue-600',
      iconColor: 'text-blue-600',
    },
  }

  const config = variants[variant]

  return (
    <div className={cn(
      'border-l-4 p-4 rounded-r-lg',
      config.bg,
      className
    )}>
      <div className="flex items-start gap-3">
        {icon && (
          <div className={cn('flex-shrink-0 mt-0.5', config.iconColor)}>
            {icon}
          </div>
        )}
        <div className="flex-1 text-sm text-left leading-relaxed">
          {children}
        </div>
      </div>
    </div>
  )
}

// ============================================
// AGENDA ITEM (Reusable Component)
// ============================================
export interface AgendaItemProps {
  number?: number
  icon?: React.ReactNode
  title: string
  description?: string
  duration?: string
  items?: string[]
}

export const AgendaItem: React.FC<AgendaItemProps> = ({
  number,
  icon,
  title,
  description,
  duration,
  items,
}) => {
  return (
    <Card className="h-full bg-white/10 dark:bg-black/10 backdrop-blur-sm">
      <CardContent>
        <div className="flex items-start gap-4 mb-3">
          {(icon || number) && (
            <div className="flex-shrink-0 w-10 h-10 rounded-lg bg-white/20 dark:bg-black/20 flex items-center justify-center font-bold">
              {icon || number}
            </div>
          )}
          <div className="flex-1">
            <div className="flex items-start justify-between mb-2">
              <h3 className="text-lg font-bold text-left">{title}</h3>
              {duration && (
                <span className="text-xs opacity-75 ml-2 flex-shrink-0">{duration}</span>
              )}
            </div>
          </div>
        </div>
        {description && (
          <p className="text-sm opacity-90 mb-3 text-left">{description}</p>
        )}
        {items && items.length > 0 && (
          <ul className="space-y-2 text-sm text-left">
            {items.map((item, index) => (
              <li key={index} className="flex items-start gap-3">
                <span className="opacity-60 mt-0.5 flex-shrink-0 leading-relaxed">•</span>
                <span className="opacity-80 flex-1 leading-relaxed">{item}</span>
              </li>
            ))}
          </ul>
        )}
      </CardContent>
    </Card>
  )
}

// ============================================
// STATS CARD (Reusable Component for metrics)
// ============================================
export interface StatsCardProps {
  icon?: React.ReactNode
  value: string | number
  label: string
  trend?: {
    value: string
    positive?: boolean
  }
}

export const StatsCard: React.FC<StatsCardProps> = ({
  icon,
  value,
  label,
  trend,
}) => {
  return (
    <Card className="text-center h-full flex flex-col justify-center">
      <CardContent>
        {icon && (
          <div className="mb-3 flex justify-center text-primary-600 dark:text-primary-400">
            {icon}
          </div>
        )}
        <div className="text-3xl font-bold mb-1">{value}</div>
        <div className="text-sm opacity-75">{label}</div>
        {trend && (
          <div className={cn(
            'text-xs mt-2',
            trend.positive ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'
          )}>
            {trend.positive ? '↑' : '↓'} {trend.value}
          </div>
        )}
      </CardContent>
    </Card>
  )
}
